/**
 * Affiliate touch: a 30-day, last-click memory of the visit that arrived from an
 * affiliate network (Adtraction, `at_gd` click id). Decision of 2026-09-24
 * (CRM ADR 2026-09-24.i).
 *
 * WHY. Our utm_* and `entryPage` cookies live 6 hours. Affiliate networks work
 * with a 30-day, last-click window: a visitor who clicks the partner link today
 * and comes back directly in three days to register must still be credited to
 * the partner. Without this module that lead arrives as "direct" and the
 * partner never hears about the sale.
 *
 * WHAT IS STORED. The attribution of the affiliate arrival only (utm_*, the
 * full landing url that carries the click id, and when it happened), in the
 * `l5_aff_touch` cookie on the shared domain (so the app at app.<domain> reads
 * the same value) and, as a backup, in `localStorage` (the CMP does not wipe
 * it). It is NEVER written as an `at_gd` cookie: the monorepo still has a
 * legacy handler that fires an old Adtraction postback when that cookie exists.
 *
 * THE RULES (pure, see `planAffiliateTouch`):
 *  - an arrival with an affiliate click id in the url SAVES (or replaces) it;
 *  - any other NEW campaign arrival CLEARS it: last click wins, so a later
 *    Google click is not overwritten by an older affiliate one;
 *  - an arrival without campaign params and without a live utm_source (a
 *    direct return) RESTORES it, so the registration carries the partner's
 *    attribution and its click id;
 *  - after 30 days it is ignored and cleared.
 *
 * The same cookie name, format and rules live in customerinterface
 * (`src/utils/affiliateTouch.ts`). Change both together.
 */
import { getCookie, setCookie } from "../components/Hooks";

export const AFFILIATE_TOUCH_COOKIE = "l5_aff_touch";
export const AFFILIATE_TOUCH_STORAGE_KEY = "l5_aff_touch";
export const AFFILIATE_TOUCH_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
/** A cookie holds ~4 KB; the landing url is the only long field. */
const MAX_ENTRY_PAGE = 1500;

/** Url params that identify a click from an affiliate network. */
export const AFFILIATE_CLICK_PARAMS = ["at_gd"] as const;

/** The utm_* restored on a direct return. */
export const AFFILIATE_UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;
export type AffiliateUtmKey = (typeof AFFILIATE_UTM_KEYS)[number];

export interface AffiliateTouch {
  v: 1;
  /** utm_* of the affiliate arrival. */
  utm: Partial<Record<AffiliateUtmKey, string>>;
  /** Full landing url, click id included. */
  entryPage: string;
  /** Epoch ms of the arrival. */
  at: number;
}

const usable = (value: string | null | undefined): value is string =>
  typeof value === "string" &&
  value.length > 0 &&
  value !== "null" &&
  value !== "undefined" &&
  value !== "defined";

/** The touch described by an affiliate arrival url, or null if it is not one. */
export const affiliateTouchFromUrl = (href: string, now: number): AffiliateTouch | null => {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const hasClickId = AFFILIATE_CLICK_PARAMS.some((p) => usable(url.searchParams.get(p)));
  const source = url.searchParams.get("utm_source");
  if (!hasClickId || !usable(source)) return null;
  const utm: AffiliateTouch["utm"] = {};
  AFFILIATE_UTM_KEYS.forEach((key) => {
    const value = url.searchParams.get(key);
    if (usable(value)) utm[key] = value;
  });
  return { v: 1, utm, entryPage: href.slice(0, MAX_ENTRY_PAGE), at: now };
};

export const serializeAffiliateTouch = (touch: AffiliateTouch): string => JSON.stringify(touch);

/** Parses a stored touch; null when absent, malformed or older than 30 days. */
export const parseAffiliateTouch = (raw: string | null | undefined, now: number): AffiliateTouch | null => {
  if (!usable(raw)) return null;
  try {
    const t = JSON.parse(raw) as AffiliateTouch;
    if (!t || t.v !== 1 || typeof t.entryPage !== "string" || typeof t.at !== "number") return null;
    if (!t.utm || !usable(t.utm.utm_source)) return null;
    if (now - t.at > AFFILIATE_TOUCH_DAYS * DAY_MS || t.at > now + DAY_MS) return null;
    return t;
  } catch {
    return null;
  }
};

export type AffiliateTouchPlan =
  | { action: "save"; touch: AffiliateTouch }
  | { action: "clear" }
  | { action: "restore"; touch: AffiliateTouch }
  | { action: "none" };

/**
 * What to do with the affiliate memory on this page load. PURE.
 *
 * @param href            current url
 * @param isNewCampaign   the url carries campaign params (utm_* / click ids) of a new arrival
 * @param hasLiveSource   a utm_source is already live for this session (cookie or seal)
 * @param stored          the touch currently stored (already parsed and in date)
 */
export const planAffiliateTouch = (input: {
  href: string;
  now: number;
  isNewCampaign: boolean;
  hasLiveSource: boolean;
  stored: AffiliateTouch | null;
}): AffiliateTouchPlan => {
  const arrival = affiliateTouchFromUrl(input.href, input.now);
  if (arrival) return { action: "save", touch: arrival };
  if (input.isNewCampaign) return input.stored ? { action: "clear" } : { action: "none" };
  if (!input.hasLiveSource && input.stored) return { action: "restore", touch: input.stored };
  return { action: "none" };
};

// ───────────────────────────── effects ─────────────────────────────

const readStorage = (): string | null => {
  try {
    return window.localStorage.getItem(AFFILIATE_TOUCH_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStorage = (value: string | null): void => {
  try {
    if (value === null) window.localStorage.removeItem(AFFILIATE_TOUCH_STORAGE_KEY);
    else window.localStorage.setItem(AFFILIATE_TOUCH_STORAGE_KEY, value);
  } catch {
    /* storage blocked: the cookie is the primary copy */
  }
};

/** The stored touch: the cookie first, the localStorage copy if the CMP wiped it. */
export const readAffiliateTouch = (now: number = Date.now()): AffiliateTouch | null =>
  parseAffiliateTouch(getCookie(AFFILIATE_TOUCH_COOKIE), now) ??
  parseAffiliateTouch(readStorage(), now);

const saveAffiliateTouch = (touch: AffiliateTouch): void => {
  const value = serializeAffiliateTouch(touch);
  setCookie(AFFILIATE_TOUCH_COOKIE, value, new Date(touch.at + AFFILIATE_TOUCH_DAYS * DAY_MS));
  writeStorage(value);
};

export const clearAffiliateTouch = (): void => {
  setCookie(AFFILIATE_TOUCH_COOKIE, null, new Date(0));
  writeStorage(null);
};

/**
 * Applies the plan. `restore` writes the affiliate utm_* and entry page through
 * the caller's writer (the attribution module owns cookies + seal), so the
 * rest of the landing (calculator, handoff to the app) sees an ordinary
 * attributed visit.
 */
export const applyAffiliateTouch = (input: {
  isNewCampaign: boolean;
  hasLiveSource: boolean;
  restore: (touch: AffiliateTouch) => void;
  now?: number;
  /** Current url; defaults to `window.location.href`. */
  href?: string;
}): AffiliateTouchPlan["action"] => {
  if (typeof window === "undefined") return "none";
  const now = input.now ?? Date.now();
  const stored = readAffiliateTouch(now);
  const plan = planAffiliateTouch({
    href: input.href ?? window.location.href,
    now,
    isNewCampaign: input.isNewCampaign,
    hasLiveSource: input.hasLiveSource,
    stored,
  });
  if (plan.action === "save") saveAffiliateTouch(plan.touch);
  else if (plan.action === "clear") clearAffiliateTouch();
  else if (plan.action === "restore") input.restore(plan.touch);
  // An expired copy is dead weight: drop it so it can't be read by mistake.
  if (plan.action !== "save" && !stored && (getCookie(AFFILIATE_TOUCH_COOKIE) || readStorage())) {
    clearAffiliateTouch();
  }
  return plan.action;
};
